import type { BillingNoticeProps } from '#shared/types/billingNotice'

type BillingNoticeContext = 'invite' | 'seat_change' | 'admin_promotion' | 'pending_invitation'

interface TrackedBillingNotice {
  context: BillingNoticeContext
  fromSeat?: Seat
  toSeat: Seat
  memberCount: number
  breakdown: BillingNoticeProps
}

/**
 * Tracks when a dialog's billing notice is seen, and when the dialog is closed without confirming while it is.
 * `getNotice` returns undefined whenever the dialog isn't showing a notice.
 */
export default function useBillingNoticeTracking(getNotice: () => TrackedBillingNotice | undefined) {
  const { track } = useAnalytics()

  const visibleNotice = computed(getNotice)

  const toProperties = ({ context, fromSeat, toSeat, memberCount, breakdown }: TrackedBillingNotice) => ({
    context,
    from_seat: fromSeat,
    to_seat: toSeat,
    member_count: memberCount,
    current_seat_total: breakdown.currentSeatTotal,
    from_available: breakdown.fromAvailable,
    from_pending: breakdown.fromPending,
    newly_purchased: breakdown.newlyPurchased,
  })

  let isViewed = false

  watch(
    visibleNotice,
    notice => {
      if (!notice || isViewed) return
      isViewed = true
      track(ANALYTICS_EVENTS.SEAT_BILLING_NOTICE_VIEWED, toProperties(notice))
    },
    { immediate: true }
  )

  const trackAbandoned = () => {
    if (!visibleNotice.value) return
    track(ANALYTICS_EVENTS.SEAT_BILLING_NOTICE_ABANDONED, toProperties(visibleNotice.value))
  }

  return { trackAbandoned }
}
